// One table: each payment-attempt state has exactly three renderings — what a
// payer is shown, what an operator is shown, and the tone that drives the
// status pill's dot.
//
// Operators keep near-state-machine wording on purpose — they are reading a
// queue and the state name is the fact they need. Payers never see one, not in
// the UI, not in a tooltip, not in a copyable error.
//
// `payer: null` means a state is withheld from the payer entirely. No state
// uses it today: the old withholding of `dispute_observed` was a vault-era
// position, and it was decided the opposite way (Kai, 2026-08-09) —
// the payer IS told, plainly, that their bank raised a dispute. Note that
// `refund_observed` and `dispute_observed` are values of the attempt's
// `providerReviewCategory`, not of `status`: the webhook worker leaves an
// already-paid attempt `paid`. The bearer projection exposes that decision as
// `payerNotice`, and the payer page maps it back to these display-only keys.
const PAYMENT_ATTEMPT_VOCABULARY = {
  pending_next_step: { payer: 'Ready to pay', operator: 'Ready', tone: 'quiet' },
  checkout_creating: { payer: 'Setting up…', operator: 'Creating checkout', tone: 'waiting' },
  checkout_created: { payer: 'Waiting for payment', operator: 'Checkout open', tone: 'waiting' },
  processing: { payer: 'Processing', operator: 'Processing', tone: 'waiting' },
  pending_confirmation: {
    payer: 'Waiting for confirmation',
    operator: 'Awaiting confirmation',
    tone: 'waiting',
  },
  paid: { payer: 'Paid', operator: 'Paid', tone: 'done' },
  expired: { payer: 'This link expired', operator: 'Expired', tone: 'quiet' },
  failed: { payer: "Payment didn't go through", operator: 'Failed', tone: 'attention' },
  needs_review: { payer: "We're checking this", operator: 'Needs review', tone: 'attention' },
  refund_observed: { payer: 'Refunded', operator: 'Refund observed', tone: 'quiet' },
  dispute_observed: { payer: 'Disputed', operator: 'Dispute observed', tone: 'attention' },
};

// Invoice lifecycle is not in the table above — it is operator vocabulary
// throughout, and the words are already plain. Only the pill tone is new.
const INVOICE_STATUS_TONES = {
  draft: 'quiet',
  issued: 'done',
  void: 'quiet',
};

export function statusLabel(status) {
  return String(status || '').replaceAll('_', ' ');
}

// Near-state-machine, per the table's operator column. Falls back to underscore
// stripping for the vocabularies the table does not cover (provider sync state,
// reconciliation category, risk observation status).
export function operatorStatusLabel(status) {
  return PAYMENT_ATTEMPT_VOCABULARY[status]?.operator || statusLabel(status);
}

// Returns '' for a state withheld from the payer (payer: null). No state is
// withheld today, but the mechanism stays: callers omit the whole status row
// rather than substituting a stand-in, so nothing has to be invented.
export function payerStatusLabel(status) {
  const entry = PAYMENT_ATTEMPT_VOCABULARY[status];
  if (!entry) return 'Saved';
  return entry.payer || '';
}

// 'quiet' | 'waiting' | 'done' | 'attention'. Named for meaning rather than
// colour so the pill, not this module, owns the palette mapping.
export function statusTone(status) {
  return PAYMENT_ATTEMPT_VOCABULARY[status]?.tone || 'quiet';
}

export function invoiceStatusTone(status) {
  return INVOICE_STATUS_TONES[status] || 'quiet';
}

export function riskKindLabel(kind) {
  return {
    refund: 'Refund',
    refund_summary: 'Charge refund total',
    dispute: 'Dispute',
  }[kind] || 'Payment risk';
}

export function paymentDisplayStatus(status, payerNotice) {
  return {
    refunded: 'refund_observed',
    disputed: 'dispute_observed',
  }[payerNotice] || status;
}

export function paymentHeading(status, checkoutReturnHint) {
  return {
    pending_next_step: 'Ready to pay',
    checkout_creating: 'Setting up your payment',
    checkout_created: checkoutReturnHint === 'cancel'
      ? 'Payment not completed'
      : 'Ready to pay',
    processing: 'Confirming your payment',
    paid: 'Payment received',
    expired: 'This payment link expired',
    failed: "Payment didn't go through",
    needs_review: 'On hold for a check',
    pending_confirmation: 'Waiting for confirmation',
    refund_observed: 'This payment was refunded',
    // The payer is told plainly (Kai, 2026-08-09). This replaced the
    // withheld 'On hold for a check' heading, which never rendered anyway.
    dispute_observed: 'This payment is disputed',
  }[status] || 'Your details are saved';
}

// Every non-success state says plainly whether money moved. That is the sentence
// a worried payer is looking for, and it is the one thing an ambiguous payment
// screen must not leave them to guess. Where the answer is genuinely not known
// yet, say that it is being checked rather than guessing in either direction.
export function paymentCopy(status, checkoutReturnHint) {
  return {
    pending_next_step:
      "Your details are saved and nothing has been charged. Continue when you're ready.",
    checkout_creating:
      "We're getting the secure payment page ready. Nothing has been charged yet.",
    checkout_created: checkoutReturnHint === 'cancel'
      ? 'You came back without paying, so nothing has been charged. You can start again whenever you like.'
      : "The secure payment page is open and nothing has been charged yet. Continue when you're ready.",
    processing:
      "Your card details have gone to the payment page and we're waiting for confirmation, so we can't say yet whether the payment went through. This page updates on its own — please don't pay again.",
    paid: 'Thanks. Your payment went through, and your receipt is below.',
    expired:
      "That payment page expired before it was used, so nothing has been charged. You can start again.",
    failed:
      "The payment didn't go through and your card wasn't charged. You can try again, or use a different card.",
    needs_review:
      "Something needs a quick check before this can go through. We can't confirm yet whether your card was charged, and the person who did the work has been notified.",
    pending_confirmation:
      "We haven't had confirmation yet, so we can't say yet whether the payment went through. This page updates as soon as we know — please don't pay again.",
    refund_observed:
      'This payment was refunded. The money has gone back to the card you used; your bank may take a few days to show it.',
    // A dispute is nearly always raised through the payer's own bank, so name
    // the bank plainly and say who acts next (Kai, 2026-08-09). The prior
    // copy pretended the payment was still being checked pre-charge.
    dispute_observed:
      "Your bank has raised a dispute on this payment, so the money is on hold while it's sorted out. Your bank will deal with you directly — there's nothing you need to do here.",
  }[status] || 'Your details are saved and nothing has been charged.';
}
